//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, CircularProgress, Grid, InputLabel, TextField, Typography, useTheme } from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import DoneIcon from '@mui/icons-material/Done'
import {
  commaDecStringToErc20Wei,
  commaDecStringToErcDecString,
  ERC20CommaDecString,
  ERC20DecString,
  erc20WeiToCommaDecString,
  otAssert,
  toCommaDecString,
} from '@shared/conversions'

import { Contract } from 'ethers'

import ButtonAction from '@react/components/elements/ButtonAction'
import { poolActions } from '@react/redux/poolSlice'
import { useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'
import { textFieldValueToERC20DecString } from '@react/utils/textFieldsUtils'

import { Erc20Abi } from '@shared/Abis'

import { ETH_ADDR_ZERO, EthAddr } from '@shared/SchemaTypes'

import { refreshBalancesThunk } from '@react/chainThunks/refreshBalancesThunk'
import { approveDepositToPoolTx } from '@react/signedTxs/approveDepositToPoolTx'
import { depositToPoolTx } from '@react/signedTxs/depositToPoolTx'
import { logOT } from '@shared/loggingShared'

export default function LenderDepositAndApproveToPool(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props

  const {
    userConnection,
    isLoggedIn,
    userConnection: { accountAddr },
    chainConfiguration: { chainConfigurationName, providerReadOnly },
  } = useOtChainState()
  otAssert(userConnection, 'userConnection is undefined')
  const { accountBalances } = userConnection
  const actionEvent = useOtSelector((state) => state.poolState.actionEvent)

  const [showSpinnerAction1, setShowSpinnerAction1] = useState<boolean>(false)
  const [showSpinnerAction2, setShowSpinnerAction2] = useState<boolean>(false)

  const [currentAllowance, setCurrentALlowance] = useState<bigint>(0n)

  const [validationMessage, setValidationMessage] = useState<string | undefined>(undefined)
  const [approvalDone, setApprovalDone] = useState<boolean>(false)
  const [depositDone, setDepositDone] = useState<boolean>(false)
  const [value, setValue] = React.useState<ERC20DecString | ERC20CommaDecString>('')
  const dispatch = useOtDispatch()
  const theme = useTheme()

  const poolOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])

  const erc20BalanceWei = accountBalances?.[poolOverviewCTO?.liquidityAssetAddr ?? ETH_ADDR_ZERO] ?? '0'

  logOT.info('poolOverviewCTO', poolOverviewCTO?.poolAddr)
  const refreshCurrentAllowance = async () => {
    if (accountAddr && chainConfigurationName) {
      logOT.info('refreshBalancesThunk')
      dispatch(refreshBalancesThunk(accountAddr as EthAddr, chainConfigurationName))
    }
    if (poolOverviewCTO) {
      const liquidityAssetContract = new Contract(
        poolOverviewCTO?.liquidityAssetAddr as EthAddr,
        Erc20Abi,
        providerReadOnly
      )
      const _currentAllowance: bigint = await liquidityAssetContract.allowance(accountAddr, poolAddr)
      logOT.info('refreshCurrentAllowance _currentAllowance', _currentAllowance)
      if (_currentAllowance !== currentAllowance) {
        setCurrentALlowance(_currentAllowance)
        checkValidation(commaDecStringToErc20Wei(value), _currentAllowance)
        logOT.info('refreshCurrentAllowance currentAllowance', _currentAllowance)
        if (_currentAllowance !== 0n) {
          setApprovalDone(true)
          setShowSpinnerAction1(false)
        }
      }
    }
  }
  useEffect(() => {
    if (accountAddr && poolAddr && providerReadOnly) {
      refreshCurrentAllowance()
        .then(() => {})
        .catch((err) => {
          logOT.error('approveDepositToPool error', err)
        })
    }
  }, [providerReadOnly, accountAddr, poolAddr])
  useEffect(() => {
    if (actionEvent === poolActions.approveDepositToPoolSuccess.type) {
      dispatch(poolActions.clearActionEvent())
      refreshCurrentAllowance()
        .then(() => {})
        .catch((err) => {
          logOT.error('approveDepositToPool error', err)
        })
    }
    if (actionEvent === poolActions.depositToPoolSuccess.type) {
      dispatch(poolActions.clearActionEvent())
      setDepositDone(true)
      setShowSpinnerAction2(false)
      const event = new Event('modalClose')
      window.dispatchEvent(event)
    }
  }, [actionEvent])

  const handleApproveDepositOnlyToPool = (): void => {
    setApprovalDone(false)
    setDepositDone(false)
    if (accountAddr && poolAddr) {
      setShowSpinnerAction1(true)

      const amountWei = commaDecStringToErc20Wei(value)
      dispatch(approveDepositToPoolTx({ poolAddr, amountWei }))
      refreshCurrentAllowance()
        .then(() => {})
        .catch((err) => {
          logOT.info('approveDepositToPool error', err)
        })
    }
  }

  const handleDepositOnlyToPool = (): void => {
    if (accountAddr && poolAddr) {
      setShowSpinnerAction2(true)

      const amountWei = commaDecStringToErc20Wei(value)
      dispatch(depositToPoolTx({ poolAddr, amountWei }))
    }
  }

  const handleChangeAllocation = (): void => {
    setApprovalDone(false)
    checkValidation(commaDecStringToErc20Wei(value), currentAllowance)
  }

  const handleCancel = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  const handleOnFocus = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setValue(commaDecStringToErcDecString(value))
    checkValidation(commaDecStringToErc20Wei(value), currentAllowance)
  }
  const handleOnBlur = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setValue(toCommaDecString(value))
    checkValidation(commaDecStringToErc20Wei(value), currentAllowance)
  }

  const checkValidation = (amountWei: string, allowance: bigint) => {
    if (approvalDone) {
      if (BigInt(amountWei) > allowance) {
        setValidationMessage('Insufficient Allowance')
      } else if (BigInt(amountWei) === 0n) {
        setValidationMessage('Investment amount cannot be 0')
      } else {
        setValidationMessage(undefined)
      }
    } else {
      if (BigInt(amountWei ?? 0) > BigInt(erc20BalanceWei ?? 0)) {
        setValidationMessage('Insufficient Balance')
      } else {
        setValidationMessage(undefined)
      }
    }
  }
  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setValue(textFieldValueToERC20DecString(event))
    checkValidation(commaDecStringToErc20Wei(textFieldValueToERC20DecString(event)), currentAllowance)
  }

  const handleMaxClick = (): void => {
    if (!erc20BalanceWei) {
      return
    }
    const maxValue = approvalDone
      ? erc20WeiToCommaDecString(currentAllowance)
      : erc20WeiToCommaDecString(erc20BalanceWei)
    setValue(maxValue)
    checkValidation(commaDecStringToErc20Wei(maxValue), currentAllowance)
  }

  const header = approvalDone ? 'Approve Deposit' : 'Approve Spending Cap'
  const subHeader = approvalDone
    ? `${erc20WeiToCommaDecString(currentAllowance)} ${poolOverviewCTO.liquidityTokenSymbol} Spending Cap Approved`
    : `${erc20WeiToCommaDecString(erc20BalanceWei)} ${poolOverviewCTO.liquidityTokenSymbol} available`

  const description = approvalDone
    ? `Please input the amount that you’d like to deposit. After you approve, this amount of ${poolOverviewCTO.liquidityTokenSymbol} will be deposited into the Loan Vault.`
    : 'Please input the spending cap amount you’d like to approve. You can then approve the loan transaction up to this amount.'

  const Header = () => (
    <>
      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', marginLeft: 1 }}>
        <Typography variant="body" align="left" margin="dense">
          {header}
        </Typography>
        <Typography variant="bodyBold" align="left" margin="dense">
          {subHeader}
        </Typography>
        <Typography variant="small" align="left" margin="dense" marginTop={3}>
          {description}
        </Typography>
      </Grid>
    </>
  )

  const actionOneTitle = approvalDone ? 'Approve the Spending Cap for deposit' : 'Approve Spending Cap'
  const actionTwoTitle = approvalDone ? 'Approve the Deposit' : 'Approve The Deposit'

  const Actions = () => (
    <>
      <Grid container item marginTop={0} marginBottom={10} xs={12} key={'top'}>
        <Grid container item marginTop={10} xs={12} key={'dd1'}>
          <Grid
            item
            xs={12}
            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}
          >
            {showSpinnerAction1 && <CircularProgress />}
            {!showSpinnerAction1 && approvalDone && (
              <DoneIcon sx={{ color: theme.palette.success.main, marginLeft: 2 }} />
            )}
            {!showSpinnerAction1 && !approvalDone && (
              <CheckBoxOutlineBlankIcon sx={{ color: theme.palette.success.main, marginLeft: 2 }} />
            )}
            <Typography variant="overline" margin="dense" sx={{ marginLeft: 4 }}>
              {actionOneTitle}
            </Typography>
          </Grid>
        </Grid>
        <Grid container item marginTop={10} xs={12} key={'dd2'} marginLeft={2}>
          <Grid
            item
            xs={12}
            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}
          >
            {showSpinnerAction2 && <CircularProgress />}
            {!showSpinnerAction2 && depositDone && <DoneIcon sx={{ color: theme.palette.success.main }} />}
            {!showSpinnerAction2 && !depositDone && (
              <CheckBoxOutlineBlankIcon sx={{ color: theme.palette.success.main }} />
            )}
            <Typography variant="overline" margin="dense" sx={{ marginLeft: 4 }}>
              {actionTwoTitle}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </>
  )

  if (approvalDone) {
    return (
      <Box py={2}>
        <Grid container spacing={2}>
          <Header />
          <Grid item xs={12} sx={{ marginLeft: 1, marginBottom: 0 }}>
            <InputLabel htmlFor="amount">Investment ({poolOverviewCTO.liquidityTokenSymbol})</InputLabel>
            <TextField
              id="amountDeposit"
              error={!!validationMessage}
              helperText={validationMessage}
              data-testid="amountDeposit"
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Investment Amount"
              value={value}
              type={'tel'}
              onFocus={handleOnFocus}
              onChange={handleOnChange}
              onBlur={handleOnBlur}
            />
            <Box sx={{ width: '300px', display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <Button
                data-testid="button_max"
                color="secondary"
                variant="outlined"
                sx={{
                  // fontSize: '0.75rem', // Smaller font size
                  padding: '4px 8px', // Adjust padding for smaller size
                  textTransform: 'none', // Prevent uppercase transformation
                }}
                onClick={handleMaxClick}
              >
                Max
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Actions />
            {isLoggedIn && !showSpinnerAction1 && (
              <ButtonAction
                disabled={!!validationMessage || value === '0' || showSpinnerAction2}
                data-testid="button_approveInvestment"
                sx={{ marginRight: '16px', paddingLeft: '12px', paddingRight: '12px' }}
                variant={'primary'}
                onClick={handleDepositOnlyToPool}
              >
                Invest
              </ButtonAction>
            )}
            {isLoggedIn && !showSpinnerAction2 && (
              <ButtonAction
                data-testid="ChangeAllocationButton"
                sx={{ marginRight: '16px' }}
                variant={'tertiary'}
                onClick={handleChangeAllocation}
              >
                Change Spending Cap
              </ButtonAction>
            )}
            {!isLoggedIn && (
              <Typography variant="bodyBold" align="center" margin="dense">
                Please connect your wallet to deposit a pool
              </Typography>
            )}
            {(!showSpinnerAction2 || !showSpinnerAction2) && (
              <ButtonAction variant={'tertiary'} onClick={handleCancel}>
                Cancel
              </ButtonAction>
            )}
          </Grid>
        </Grid>
      </Box>
    )
  } else {
    return (
      <Box py={2}>
        <Grid container spacing={2}>
          <Header />
          <Grid item xs={12} sx={{ marginBottom: 0, marginLeft: 1 }}>
            <InputLabel htmlFor="amount" sx={{ marginBottom: 1 }}>
              Spending Cap ({poolOverviewCTO.liquidityTokenSymbol})
            </InputLabel>
            <TextField
              id="amount"
              error={!!validationMessage}
              helperText={validationMessage}
              data-testid="input_spendingCap"
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Investment Amount"
              value={value}
              type={'tel'}
              onFocus={handleOnFocus}
              onChange={handleOnChange}
              onBlur={handleOnBlur}
            />
            <Box sx={{ width: '300px', display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <Button
                data-testid="button_max"
                color="secondary"
                variant="outlined"
                sx={{
                  // fontSize: '0.75rem', // Smaller font size
                  padding: '4px 8px', // Adjust padding for smaller size
                  textTransform: 'none', // Prevent uppercase transformation
                }}
                onClick={handleMaxClick}
              >
                Max
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Actions />
            {isLoggedIn && !showSpinnerAction1 && (
              <ButtonAction
                disabled={!!validationMessage}
                data-testid="button_approveSpendingCap"
                sx={{ marginRight: '16px' }}
                variant={'primary'}
                onClick={handleApproveDepositOnlyToPool}
              >
                Approve Spending Cap
              </ButtonAction>
            )}
            {!isLoggedIn && (
              <Typography variant="bodyBold" align="center" margin="dense">
                Please connect your wallet to deposit a pool
              </Typography>
            )}
            {!showSpinnerAction1 && (
              <ButtonAction variant={'tertiary'} onClick={handleCancel}>
                Cancel
              </ButtonAction>
            )}
          </Grid>
        </Grid>
      </Box>
    )
  }
}
